<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { formatNumber } from "@core/helpers"
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import { ComparatorResultOfferItemCellValues } from "@types"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"

const comparatorStore = useComparatorStore()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: ({ named }: { named: (key: string) => string }) =>
                named("actual")
                    ? "Offre de référence"
                    : `Offre alternative ${named("index")}`,
            optimal: "Offre optimale",
            other_supplier: "Autre",
            attached: "Épingler",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Interface pour une ligne du tableau de données
 */
interface TableRow {
    name: string
    data: ComparatorResultOfferItemCellValues
    frozen?: boolean
    id: string
    plages?: Omit<TableRow, "plages">[]
}

/**
 * Interface pour la configuration d'une colonne du tableau
 */
interface TableColumn {
    field: string
    header: string
    tooltip?: string
    sortable?: boolean
    style?: Record<string, string>
    format?: (value: number) => string
}

/**
 * Interface pour l'état du tri
 */
interface SortState {
    field: string | null
    order: number | null
}

/**
 * Props du composant ComparatorResultOfferItem
 */
interface Props {
    /** Indique si c'est l'offre actuelle (de référence) */
    actual: boolean
    /** Index de l'offre si offre alternative */
    index: number
    /** Lignes du tableau de données */
    rows: TableRow[]
    /** ID de l'offre */
    nomOffre: string
    /** Identifiant du fournisseur de l'offre */
    supplier?: string
    /** Indique si l'offre est optimale */
    optimal?: boolean
    /** Indique si l'offre est attachée */
    attached: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    actual: false,
    index: 0,
    supplier: undefined,
    rows: () => [],
    nomOffre: "",
    attached: false,
    optimal: false,
})

defineEmits<{
    (e: "toggle:attached"): void
}>()

// ================================
// Gestion des données du tableau principale
// ================================
/**
 * Configuration des colonnes du tableau de données
 */
const getColumns = computed((): TableColumn[] => {
    return [
        {
            field: "idperiode",
            header: "",
            style: {
                width: "5rem",
                padding: "6px",
            },
        },
        {
            field: "prixHTT_elecsoutirage_abonnement",
            header: "Abo.",
            tooltip: "Prix HTT lié à l'abonnement (€ HTT)",
            sortable: true,
            format: (value: number) =>
                formatNumber(value, {
                    defaultValue: "-",
                    style: "currency",
                }),
        },
        {
            field: "prixHTT_elecsoutirage_consommation",
            header: "Fourniture",
            tooltip: "Prix HTT lié à la consommation (€ HTT)",
            sortable: true,
            format: (value: number) =>
                formatNumber(value, {
                    defaultValue: "-",
                    style: "currency",
                }),
        },
        {
            field: "prixHTT_elecsoutirage_turpe",
            header: "Turpe",
            tooltip: "Prix HTT lié au TURPE (€ HTT)",
            sortable: true,
            format: (value: number) =>
                formatNumber(value, {
                    defaultValue: "-",
                    style: "currency",
                }),
        },
        {
            field: "prixHTT_elecsoutirage_cee",
            header: "CEE",
            tooltip:
                "Prix HTT lié aux CEE (Certificats d'Économie d'Énergie) (€ HTT)",
            sortable: true,
            format: (value: number) =>
                formatNumber(value, {
                    defaultValue: "-",
                    style: "currency",
                }),
        },
        {
            field: "prixHTT_elecsoutirage_oc",
            header: "Capacité",
            tooltip: "Prix HTT lié à l'obligation de capacité (€ HTT)",
            sortable: true,
            format: (value: number) =>
                formatNumber(value, {
                    defaultValue: "-",
                    style: "currency",
                }),
        },
        {
            field: "prixHTVA_elecsoutirage_tout",
            header: "Total HTVA",
            tooltip: "Prix HTVA de toutes les composantes confondues (€ HTVA)",
            sortable: true,
            format: (value: number) =>
                formatNumber(value, {
                    defaultValue: "-",
                    style: "currency",
                }),
        },
        {
            field: "prixTTC_elecsoutirage_tout",
            header: "Total TTC",
            tooltip: "Prix TTC de toutes les composantes confondues (€ TTC)",
            sortable: true,
            format: (value: number) =>
                formatNumber(value, {
                    defaultValue: "-",
                    style: "currency",
                }),
        },
    ]
})

/**
 * Lignes du tableau non mises en évidence (triables)
 */
const getRows = computed(() => {
    return props.rows
        .filter((row: TableRow) => !row?.frozen)
        .map((row: TableRow) => ({
            id: row.id,
            idperiode: row.name,
            ...row.data,
            plages: row.plages
                ?.filter((plage) => !plage?.frozen)
                .map((plage) => ({
                    id: plage.id,
                    idperiode: plage.name,
                    ...plage.data,
                })),
        }))
})

/**
 * Lignes du tableau mises en évidence (figées en bas)
 */
const getFrozenRows = computed(() => {
    return props.rows
        .filter((row: TableRow) => row?.frozen)
        .map((row: TableRow) => ({
            idperiode: row.name,
            ...row.data,
            options: {
                frozen: row.frozen,
            },
        }))
})

/**
 * Données du tableau triées selon l'état actuel du tri
 */
const getSortedData = computed(() => {
    const sortableData = [...getRows.value]
    const frozenRows = [...getFrozenRows.value]

    // Appliquer le tri seulement aux données non-figées
    if (currentSort.value.field && currentSort.value.order) {
        sortableData.sort((a, b) => {
            const aVal = a[currentSort.value.field as keyof typeof a]
            const bVal = b[currentSort.value.field as keyof typeof b]

            // Gérer les valeurs null/undefined
            if (aVal == null && bVal == null) return 0
            if (aVal == null) return currentSort.value.order === 1 ? 1 : -1
            if (bVal == null) return currentSort.value.order === 1 ? -1 : 1

            // Comparaison numérique pour les nombres
            if (typeof aVal === "number" && typeof bVal === "number") {
                return currentSort.value.order === 1 ? aVal - bVal : bVal - aVal
            }

            // Comparaison par défaut pour les autres types
            if (currentSort.value.order === 1) {
                return aVal > bVal ? 1 : -1
            } else {
                return aVal < bVal ? 1 : -1
            }
        })
    }

    // Retourner les données triées en premier, puis les lignes figées en bas
    return [...sortableData, ...frozenRows]
})

/**
 * Détermine les classes CSS pour une ligne du tableau
 * @param data - Les données de la ligne
 * @returns Les classes CSS à appliquer
 */
function getRowClass(data: {
    options?: { frozen?: boolean }
}): Record<string, boolean>[] {
    return [
        {
            "!bg-gray-50": data?.options?.frozen === true,
        },
    ]
}

// ================================
// Gestion du tri du tableau
// ================================
/**
 * État actuel du tri du tableau
 */
const currentSort = ref<SortState>({
    field: null,
    order: null,
})

const clickSort = ref({
    field: "",
    click: 0,
})
/**
 * Gère le tri d'une colonne du tableau
 * @param field - Le champ à trier
 */
function onColumnSort(field: string): void {
    if (clickSort.value.field !== field) {
        clickSort.value = {
            field: field,
            click: 0,
        }
    }
    if (clickSort.value.click >= 2) {
        currentSort.value = {
            field: null,
            order: null,
        }
        clickSort.value.click = 0
        return
    }

    // Incrémenter le compteur de clics
    clickSort.value.click += 1

    if (currentSort.value.field === field) {
        // Si on clique sur la même colonne, inverser l'ordre
        currentSort.value.order = currentSort.value.order === 1 ? -1 : 1
    } else {
        // Nouvelle colonne, commencer par tri ascendant
        currentSort.value = {
            field: field,
            order: 1,
        }
    }
}

const getSupplier = computed(() => {
    return comparatorStore.suppliers.find(
        (supplier) => supplier._id_supplier === props.supplier
    )
})

// ================================
// Gestion du tableau étendu
// ================================
const expandedRows = ref<Record<string, boolean>>({})

function toggleRow(rowData: TableRow) {
    if (expandedRows.value[rowData.id]) {
        delete expandedRows.value[rowData.id]
    } else {
        expandedRows.value[rowData.id] = true
    }
}

function isExpanded(rowData: TableRow): boolean {
    return !!expandedRows.value[rowData.id]
}

/**
 * Données du tableau triées selon l'état actuel du tri
 */
function computedSortedExtendedData(plages: TableRow[]) {
    const sortableData = [...plages]

    // Appliquer le tri seulement aux données non-figées
    if (currentSortExpanded.value.field && currentSortExpanded.value.order) {
        sortableData.sort((a, b) => {
            const aVal = a[currentSortExpanded.value.field as keyof typeof a]
            const bVal = b[currentSortExpanded.value.field as keyof typeof b]

            // Gérer les valeurs null/undefined
            if (aVal == null && bVal == null) return 0
            if (aVal == null)
                return currentSortExpanded.value.order === 1 ? 1 : -1
            if (bVal == null)
                return currentSortExpanded.value.order === 1 ? -1 : 1

            // Comparaison numérique pour les nombres
            if (typeof aVal === "number" && typeof bVal === "number") {
                return currentSortExpanded.value.order === 1
                    ? aVal - bVal
                    : bVal - aVal
            }

            // Comparaison par défaut pour les autres types
            if (currentSortExpanded.value.order === 1) {
                return aVal > bVal ? 1 : -1
            } else {
                return aVal < bVal ? 1 : -1
            }
        })
    }

    // Retourner les données triées en premier, puis les lignes figées en bas
    return [...sortableData]
}

// ================================
// Gestion du tri du tableau étendu
// ================================
/**
 * État actuel du tri du tableau
 */
const currentSortExpanded = ref<SortState>({
    field: null,
    order: null,
})

/**
 * Compteur de clics pour le tri étendu
 */
const clickSortExpanded = ref({
    field: "",
    click: 0,
})

/**
 * Quand on clique sur une colonne du tableau étendu
 * @param field - Le champ à trier
 * @param sortFieldId - L'identifiant du champ de tri
 */
function onColumnExpandedSort(field: string): void {
    if (clickSortExpanded.value.field !== field) {
        clickSortExpanded.value = {
            field: field,
            click: 0,
        }
    }
    if (clickSortExpanded.value.click >= 2) {
        currentSortExpanded.value = {
            field: null,
            order: null,
        }
        clickSortExpanded.value.click = 0
        return
    }

    // Incrémenter le compteur de clics
    clickSortExpanded.value.click += 1

    if (currentSortExpanded.value.field === field) {
        // Si on clique sur la même colonne, inverser l'ordre
        currentSortExpanded.value.order =
            currentSortExpanded.value.order === 1 ? -1 : 1
    } else {
        // Nouvelle colonne, commencer par tri ascendant
        currentSortExpanded.value = {
            field: field,
            order: 1,
        }
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL - ACCORDÉON D'OFFRE -->
    <!-- ================================ -->
    <BaseAccordion value="0" class="comparator-result-offer-item">
        <BaseAccordionPanel value="0">
            <!-- ================================ -->
            <!-- HEADER - INFORMATIONS DE L'OFFRE -->
            <!-- ================================ -->
            <BaseAccordionHeader
                class="comparator-offer-item-info-general__header flex-row-reverse! justify-end! gap-2!"
            >
                <div class="flex items-center gap-2 justify-between w-full">
                    <!-- SECTION GAUCHE - IDENTIFICATION DE L'OFFRE -->
                    <div class="flex items-center gap-2">
                        <!-- Type d'offre -->
                        <p class="text-sm text-cyan-500">
                            {{
                                t("title", {
                                    actual,
                                    index,
                                })
                            }}
                        </p>

                        <!-- Séparateur -->
                        <span class="text-sm text-cyan-500" v-if="nomOffre"
                            >•</span
                        >

                        <!-- Titre alternatif si ID présent -->
                        <p class="text-sm text-gray-700" v-if="nomOffre">
                            {{ nomOffre }}
                        </p>
                        <span class="text-sm text-cyan-500">•</span>

                        <!-- Logo du fournisseur -->
                        <div
                            class="w-7 h-7 rounded-md flex items-center justify-center text-sm font-light text-gray-700"
                            :class="
                                !getSupplier?.img
                                    ? 'bg-gray-200'
                                    : 'bg-transparent'
                            "
                        >
                            <span v-if="!getSupplier?.img">F</span>
                            <img
                                v-else-if="getSupplier?.img"
                                :src="getSupplier.img"
                                :alt="getSupplier.name"
                                class="w-7 h-7 rounded-md object-contain"
                            />
                        </div>

                        <!-- Nom du fournisseur -->
                        <p class="text-sm text-gray-700">
                            {{ getSupplier?.name ?? t("other_supplier") }}
                        </p>
                    </div>

                    <!-- SECTION DROITE - ACTIONS ET STATUTS -->
                    <div class="flex items-center gap-2">
                        <!-- Badge offre optimale -->
                        <BaseTag
                            v-if="optimal"
                            icon="pi pi-star-fill"
                            severity="success"
                            :pt="{
                                root: 'px-3 py-[6px]',
                            }"
                        >
                            {{ t("optimal") }}
                        </BaseTag>

                        <!-- Bouton d'attachement -->
                        <BaseButton
                            v-if="actual"
                            rounded
                            icon="pi pi-paperclip"
                            variant="link"
                            v-tooltip.top="t('attached')"
                            :pt="{
                                root: attached
                                    ? 'bg-[#35c4dc29]!'
                                    : 'bg-transparent',
                            }"
                            @click.prevent.stop="$emit('toggle:attached')"
                        />
                    </div>
                </div>
            </BaseAccordionHeader>

            <!-- ================================ -->
            <!-- CONTENU - TABLEAU DES DONNÉES FINANCIÈRES -->
            <!-- ================================ -->
            <BaseAccordionContent>
                <BaseDataTable
                    :value="getSortedData"
                    :row-class="getRowClass"
                    v-model:expanded-rows="expandedRows"
                    table-style="min-width: 50rem"
                    class="bottom-frozen-table"
                    removable-sort
                    data-key="id"
                >
                    <BaseColumn expander style="width: 3rem">
                        <template #body="slotProps">
                            <button
                                v-if="
                                    slotProps.data.plages &&
                                    slotProps.data.plages.length
                                "
                                @click="toggleRow(slotProps.data)"
                                class="p-row-toggler p-link"
                                type="button"
                            >
                                <i
                                    :class="
                                        isExpanded(slotProps.data)
                                            ? 'pi pi-chevron-down'
                                            : 'pi pi-chevron-right'
                                    "
                                ></i>
                            </button> </template
                    ></BaseColumn>
                    <!-- COLONNES DYNAMIQUES -->
                    <BaseColumn
                        v-for="column in getColumns"
                        :key="column.field"
                        :field="column.field"
                        :style="{
                            ...column.style,
                            height: '3.25rem',
                            maxHeight: '3.5rem',
                        }"
                    >
                        <!-- HEADER DE COLONNE AVEC TRI -->
                        <template #header>
                            <div
                                class="flex items-center gap-4"
                                :class="column.sortable ? 'cursor-pointer' : ''"
                                @click="
                                    column.sortable
                                        ? onColumnSort(column.field)
                                        : null
                                "
                            >
                                <!-- Titre et tooltip -->
                                <h6
                                    class="font-bold text-sm flex items-center gap-2"
                                >
                                    {{ column.header }}
                                    <i
                                        v-if="column.tooltip"
                                        class="pi pi-info-circle"
                                        v-tooltip.top="column.tooltip"
                                        style="font-size: 0.8rem"
                                    ></i>
                                </h6>

                                <!-- Indicateurs de tri -->
                                <i
                                    v-if="
                                        column.sortable &&
                                        currentSort.field === column.field
                                    "
                                    :class="
                                        currentSortExpanded.order === 1
                                            ? 'pi pi-sort-amount-up'
                                            : currentSort.order === -1
                                              ? 'pi pi-sort-amount-down'
                                              : 'pi pi-sort-alt'
                                    "
                                ></i>
                                <i
                                    v-else-if="column.sortable"
                                    class="pi pi-sort-alt text-gray-400"
                                ></i>
                            </div>
                        </template>

                        <!-- CELLULE DE DONNÉES -->
                        <template #body="{ data }">
                            <p
                                class="text-sm"
                                :class="
                                    column.field === 'idperiode'
                                        ? 'font-bold'
                                        : ''
                                "
                            >
                                {{
                                    column.format
                                        ? column.format(data[column.field])
                                        : data[column.field]
                                }}
                            </p>
                            <p
                                v-if="
                                    column.field ===
                                        'prixHTT_elecsoutirage_consommation' &&
                                    data['prixHTVAparkWh_elecsoutirage_tout'] !=
                                        null
                                "
                                class="text-xs text-gray-500 mt-1"
                            >
                                ({{
                                    formatNumber(
                                        data[
                                            "prixHTVAparkWh_elecsoutirage_tout"
                                        ] * 1000,
                                        {
                                            defaultValue: "-",
                                            style: "decimal",
                                            unit: "€/MWh",
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 0,
                                        }
                                    )
                                }})
                            </p>
                            <p
                                v-if="
                                    column.field ===
                                        'prixTTC_elecsoutirage_tout' &&
                                    data['vaprixTTC_elecsoutirage_tout'] !=
                                        null &&
                                    Math.abs(
                                        data['vaprixTTC_elecsoutirage_tout']
                                    ) > 0.001 &&
                                    data['idperiode'] !== 'Total'
                                "
                                :class="[
                                    'text-xs mt-1',
                                    data['vaprixTTC_elecsoutirage_tout'] < 0
                                        ? 'text-green-600'
                                        : 'text-red-600',
                                ]"
                            >
                                ({{
                                    formatNumber(
                                        data["vaprixTTC_elecsoutirage_tout"],
                                        {
                                            defaultValue: "-",
                                            style: "decimal",
                                            unit: "€",
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 0,
                                        }
                                    )
                                }})
                            </p>
                        </template>
                    </BaseColumn>

                    <!-- TEMPLATE D'EXPANSION DES LIGNES -->
                    <template #expansion="slotProps">
                        <div class="">
                            <BaseDataTable
                                :value="
                                    computedSortedExtendedData(
                                        slotProps.data.plages
                                    )
                                "
                                :row-class="getRowClass"
                                table-style="min-width: 50rem"
                                class="bottom-frozen-table"
                                removable-sort
                            >
                                <!-- COLONNES DYNAMIQUES -->
                                <BaseColumn
                                    v-for="column in getColumns"
                                    :key="column.field"
                                    :field="column.field"
                                    :style="{
                                        ...column.style,
                                        height: '3.25rem',
                                        maxHeight: '3.5rem',
                                    }"
                                >
                                    <!-- HEADER DE COLONNE AVEC TRI -->
                                    <template #header>
                                        <div
                                            class="flex items-center gap-4"
                                            :class="
                                                column.sortable
                                                    ? 'cursor-pointer'
                                                    : ''
                                            "
                                            @click="
                                                column.sortable
                                                    ? onColumnExpandedSort(
                                                          column.field
                                                      )
                                                    : null
                                            "
                                        >
                                            <!-- Titre et tooltip -->
                                            <h6
                                                class="font-bold text-sm flex items-center gap-2"
                                            >
                                                {{ column.header }}
                                                <i
                                                    v-if="column.tooltip"
                                                    class="pi pi-info-circle"
                                                    v-tooltip.top="
                                                        column.tooltip
                                                    "
                                                    style="font-size: 0.8rem"
                                                ></i>
                                            </h6>

                                            <!-- Indicateurs de tri -->
                                            <i
                                                v-if="
                                                    column.sortable &&
                                                    currentSortExpanded.field ===
                                                        column.field
                                                "
                                                :class="
                                                    currentSortExpanded.order ===
                                                    1
                                                        ? 'pi pi-sort-amount-up'
                                                        : currentSortExpanded.order ===
                                                            -1
                                                          ? 'pi pi-sort-amount-down'
                                                          : 'pi pi-sort-alt'
                                                "
                                            ></i>
                                            <i
                                                v-else-if="column.sortable"
                                                class="pi pi-sort-alt text-gray-400"
                                            ></i>
                                        </div>
                                    </template>

                                    <!-- CELLULE DE DONNÉES -->
                                    <template #body="{ data }">
                                        <p
                                            class="text-sm"
                                            :class="
                                                column.field === 'idperiode'
                                                    ? 'font-bold'
                                                    : ''
                                            "
                                        >
                                            {{
                                                column.format
                                                    ? column.format(
                                                          data[column.field]
                                                      )
                                                    : data[column.field]
                                            }}
                                        </p>
                                        <p
                                            v-if="
                                                column.field ===
                                                    'prixHTT_elecsoutirage_consommation' &&
                                                data[
                                                    'prixHTVAparkWh_elecsoutirage_tout'
                                                ] != null
                                            "
                                            class="text-xs text-gray-500 mt-1"
                                        >
                                            ({{
                                                formatNumber(
                                                    data[
                                                        "prixHTVAparkWh_elecsoutirage_tout"
                                                    ] * 1000,
                                                    {
                                                        defaultValue: "-",
                                                        style: "decimal",
                                                        unit: "€/MWh",
                                                        maximumFractionDigits: 2,
                                                        minimumFractionDigits: 0,
                                                    }
                                                )
                                            }})
                                        </p>
                                    </template>
                                </BaseColumn>
                            </BaseDataTable>
                        </div>
                    </template>
                </BaseDataTable>
            </BaseAccordionContent>
        </BaseAccordionPanel>
    </BaseAccordion>
</template>
